/**
 * Seção "Respostas do questionário" no detalhe do lead (PR-M1-01e).
 *
 * **Por que arquivo separado** da `LeadDetailPage`: a página detail
 * já está em 418 linhas (dívida pré-existente, acima do hard limit
 * de 300 para .tsx). Surfaçar respostas adiciona ~150 linhas — em vez
 * de engordar a dívida, mantemos este componente focado e a detail
 * só o consome em 1 linha. Quando for tempo de refatorar a detail,
 * ela já estará preparada.
 *
 * **Escopo funcional**:
 *   - Lista submissões do lead (qualquer `form_type`) ordenadas por
 *     `submitted_at` desc (respondidas no topo).
 *   - Cada submissão é um `<details>` nativo — HTML semântico,
 *     acessível (Tab/Enter/Space), mobile-friendly. A mais recente
 *     abre por default; outras ficam colapsadas para não poluir.
 *   - Estado submetido mostra `FormAnswersView` com o `answers`;
 *     não-submetido (in_progress/pending/expired) mostra os
 *     `partial_answers` com aviso do estado.
 *
 * **Acessibilidade**: `<details>` dá rebatimento de teclado e leitores
 * de tela grátis; título do `<summary>` é primeira coisa ouvida.
 *
 * **Mobile-first**: layout `flex-col` em mobile, `flex-row` em sm+
 * no header do summary; tap target do summary é o trilho completo
 * (≥ 44px via padding).
 */
import { Link } from 'react-router-dom';
import { Skeleton } from '@kmgestao/ui';

import { FormAnswersView } from '../../../features/forms/renderer/FormAnswersView.js';
import {
  parseTemplateSchema,
  useLeadFormSubmissions,
  type LeadFormSubmission,
} from '../../../features/forms/queries.js';

export interface LeadFormSubmissionsSectionProps {
  leadId: string;
}

const STATUS_LABELS: Record<LeadFormSubmission['status'], string> = {
  pending: 'Aguardando',
  in_progress: 'Em andamento',
  submitted: 'Respondido',
  expired: 'Expirado',
};

function statusToneClasses(status: LeadFormSubmission['status']): string {
  // Tokens do DS — nada de hex. `submitted` usa `accent` (gold do
  // brand book) para destacar positivamente; `expired` usa
  // `destructive`; pending/in_progress ficam neutros com `muted`.
  switch (status) {
    case 'submitted':
      return 'border-accent/40 bg-accent/10 text-accent-foreground';
    case 'expired':
      return 'border-destructive/30 bg-destructive/10 text-destructive';
    case 'in_progress':
      return 'border-border bg-muted text-foreground';
    case 'pending':
    default:
      return 'border-border bg-muted/50 text-muted-foreground';
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function SubmissionItem({
  submission,
  defaultOpen,
}: {
  submission: LeadFormSubmission;
  defaultOpen: boolean;
}) {
  const schema = parseTemplateSchema(submission.template.schema);
  const answers =
    submission.status === 'submitted'
      ? (submission.answers ?? {})
      : (submission.partial_answers ?? {});
  const hasAnswers = Object.keys(answers).length > 0;
  const referenceDate =
    submission.status === 'submitted' && submission.submitted_at
      ? submission.submitted_at
      : submission.updated_at;

  return (
    <details
      className="group overflow-hidden rounded-lg border border-border bg-card transition-colors open:shadow-sm"
      open={defaultOpen}
    >
      <summary className="flex cursor-pointer list-none flex-col gap-2 p-4 hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="truncate text-sm font-semibold text-foreground sm:text-base">
            {submission.template.name_pt_br}
          </p>
          <p className="text-xs text-muted-foreground">
            {submission.status === 'submitted' ? 'Respondido em ' : 'Atualizado em '}
            {formatDateTime(referenceDate)} · v{submission.template_version}
          </p>
        </div>
        <span
          className={`inline-flex w-fit shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-medium ${statusToneClasses(
            submission.status,
          )}`}
        >
          {STATUS_LABELS[submission.status]}
        </span>
      </summary>

      <div className="border-t border-border bg-background/40 p-4 sm:p-5">
        {!hasAnswers ? (
          <p className="text-sm text-muted-foreground">Nenhuma resposta registrada até agora.</p>
        ) : schema ? (
          <>
            {submission.status !== 'submitted' ? (
              <p className="mb-4 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                O lead ainda não finalizou o envio — estes são os dados salvos automaticamente
                enquanto ele preenchia.
              </p>
            ) : null}
            <FormAnswersView
              schema={schema}
              answers={answers}
              idPrefix={`lead-sub-${submission.id}`}
            />
          </>
        ) : (
          <p className="text-sm text-destructive">
            Não foi possível renderizar as respostas — o schema deste template está em formato
            inesperado. Contate o time técnico.
          </p>
        )}
      </div>
    </details>
  );
}

export function LeadFormSubmissionsSection({ leadId }: LeadFormSubmissionsSectionProps) {
  const submissions = useLeadFormSubmissions(leadId);

  if (submissions.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-live="polite">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (submissions.isError) {
    return (
      <div
        className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
        role="alert"
      >
        Não foi possível carregar as respostas agora.{' '}
        {submissions.error instanceof Error ? submissions.error.message : null}
      </div>
    );
  }

  const rows = submissions.data ?? [];

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
        <p className="text-sm font-medium text-foreground">
          Nenhum questionário respondido ou em andamento.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Use as ações acima para enviar o questionário — assim que o lead responder, tudo aparece
          aqui com timeline de auto-save.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Para gerar um link público (Fluxo B), volte para a{' '}
          <Link to="/superadmin/leads" className="underline hover:text-foreground">
            lista de leads
          </Link>{' '}
          e clique em &quot;Gerar link de auto-cadastro&quot;.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((submission, idx) => (
        <SubmissionItem
          key={submission.id}
          submission={submission}
          // Primeira (mais recente) aberta; demais fechadas — Karla
          // escolhe se quer conferir histórico.
          defaultOpen={idx === 0}
        />
      ))}
    </div>
  );
}
